import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, MessageSquare, Send } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/sonner';

const eventLabel = (value) => String(value || '').replaceAll('.', ' · ').replaceAll('_', ' ');

export function ManualSmsDialog({ open, onOpenChange, customerId, jobId = '', onSent }) {
  const [context, setContext] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState('');
  const [overrideConsent, setOverrideConsent] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const selectedTemplate = useMemo(() => (context?.templates || []).find((template) => template.id === templateId), [context, templateId]);
  const consentState = context?.consent_state || {};
  const needsOverride = !consentState.granted && !consentState.opted_out;

  useEffect(() => {
    if (!open || !customerId) return;
    let mounted = true;
    async function loadContext() {
      setLoading(true); setError(''); setPreview(''); setMessage(''); setOverrideConsent(false); setOverrideReason('');
      try {
        const { data } = await api.get(`/customers/${customerId}/sms-context`, { params: jobId ? { job_id: jobId } : {} });
        if (!mounted) return;
        setContext(data);
        const preferred = data.templates.find((template) => template.event_type === (jobId ? 'manual.job' : 'manual.customer')) || data.templates[0];
        setTemplateId(preferred?.id || '');
      } catch (err) {
        if (mounted) setError(formatApiError(err.response?.data?.detail));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadContext();
    return () => { mounted = false; };
  }, [customerId, jobId, open]);

  useEffect(() => {
    if (!open || !customerId || !templateId) return;
    let mounted = true;
    async function loadPreview() {
      try {
        const { data } = await api.post(`/customers/${customerId}/sms-preview`, { template_id: templateId, job_id: jobId, message });
        if (mounted) setPreview(data.rendered_body || '');
      } catch (err) {
        if (mounted) setPreview('');
      }
    }
    loadPreview();
    return () => { mounted = false; };
  }, [customerId, jobId, message, open, templateId]);

  const sendSms = async () => {
    setSending(true); setError('');
    try {
      const { data } = await api.post(`/customers/${customerId}/send-sms`, { template_id: templateId, job_id: jobId, message, override_consent: overrideConsent, override_reason: overrideReason });
      toast.success(`SMS ${data.status || 'sent'} to ${data.to}`);
      onOpenChange(false);
      onSent?.(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="manual-sms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><MessageSquare className="h-5 w-5" /> Send SMS</DialogTitle>
          <DialogDescription>Select a template, preview it, then send to the customer.</DialogDescription>
        </DialogHeader>
        {error && <p className="rounded-sm border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="manual-sms-error">{error}</p>}
        {loading ? <p className="text-sm text-zinc-500" data-testid="manual-sms-loading">Loading SMS context...</p> : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 rounded-sm border border-zinc-200 bg-zinc-50 p-3" data-testid="manual-sms-consent-status">
              {consentState.granted ? <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" /> Consent on file</Badge> : consentState.opted_out ? <Badge className="border border-red-200 bg-red-50 text-red-700"><AlertTriangle className="mr-1 h-3 w-3" /> Opted out</Badge> : <Badge className="border border-amber-200 bg-amber-50 text-amber-800"><AlertTriangle className="mr-1 h-3 w-3" /> Missing consent</Badge>}
              <span className="text-xs text-zinc-500">To: {context?.customer?.phone || 'No phone'}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label>Template</Label><select value={templateId} onChange={(event) => setTemplateId(event.target.value)} className="h-9 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm" data-testid="manual-sms-template-select">{(context?.templates || []).map((template) => <option key={template.id} value={template.id}>{template.name} · {eventLabel(template.event_type)}</option>)}</select></div>
              <div className="space-y-1"><Label>Template Type</Label><Input value={eventLabel(selectedTemplate?.event_type)} readOnly data-testid="manual-sms-template-type" /></div>
            </div>
            <div className="space-y-1"><Label>Message Insert</Label><Textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Optional details inserted into {{message}} templates" rows={3} data-testid="manual-sms-message-input" /></div>
            {needsOverride && <div className="rounded-sm border border-amber-200 bg-amber-50 p-3" data-testid="manual-sms-override-panel"><label className="flex items-start gap-2 text-sm text-amber-900"><input type="checkbox" checked={overrideConsent} onChange={(event) => setOverrideConsent(event.target.checked)} data-testid="manual-sms-override-checkbox" /><span>Admin override: send without recorded consent and log the reason.</span></label>{overrideConsent && <Input value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} placeholder="Override reason" className="mt-2" data-testid="manual-sms-override-reason-input" />}</div>}
            <div className="rounded-sm border border-zinc-200 bg-white p-3" data-testid="manual-sms-preview"><p className="mb-1 text-xs font-black uppercase tracking-wider text-zinc-500">Preview</p><p className="whitespace-pre-wrap text-sm text-zinc-800">{preview || 'Select a template to preview.'}</p></div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="manual-sms-cancel-button">Cancel</Button>
          <Button type="button" onClick={sendSms} disabled={loading || sending || consentState.opted_out || (needsOverride && !overrideConsent)} data-testid="manual-sms-send-button"><Send className="mr-1 h-4 w-4" /> {sending ? 'Sending...' : 'Send SMS'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}